import {get, post} from "../../../../../assets/js/shared/data-manager";

const FAVORITE_STATE_CHANGED_EVENT = 'favorites:state-changed';

class FavoritesManagerClass {
    constructor() {
        this.favorites = {};
        this.fetchPromises = {};
        this.fetchRequestIds = {};
        this.togglePromises = {};
    }

    /**
     * Get all favorites for a publication type.
     *
     * @param {Number|String} publicationType
     * @param {boolean} forceRefresh
     * @return {Promise<Array>}
     */
    async getFavorites(publicationType, forceRefresh = false) {
        const type = String(publicationType);

        if (!forceRefresh && Array.isArray(this.favorites[type])) {
            return this.favorites[type];
        }

        if (!forceRefresh && this.fetchPromises[type]) {
            return this.fetchPromises[type];
        }

        const requestId = (this.fetchRequestIds[type] ?? 0) + 1;
        this.fetchRequestIds[type] = requestId;

        const fetchPromise = (async () => {
            try {
                const favorites = await this.fetchFavorites(type);

                if (this.fetchRequestIds[type] === requestId) {
                    this.favorites[type] = favorites;
                }

                return favorites;
            } finally {
                if (this.fetchPromises[type] === fetchPromise) {
                    this.fetchPromises[type] = null;
                }
            }
        })();

        this.fetchPromises[type] = fetchPromise;

        return fetchPromise;
    }

    /**
     * Toggle favorite state for a publication.
     *
     * @param {Number|String} publicationType
     * @param {String} publicationId
     * @return {Promise<{state: boolean}>}
     */
    async togglePublication(publicationType, publicationId) {
        const type = String(publicationType);
        const key = `${type}:${publicationId}`;

        if (this.togglePromises[key]) {
            return this.togglePromises[key];
        }

        this.togglePromises[key] = this.togglePublicationRequest(type, publicationId);

        try {
            return await this.togglePromises[key];
        } finally {
            this.togglePromises[key] = null;
        }
    }

    /**
     * Fetch favorites from the backend.
     *
     * @param {String} publicationType
     * @return {Promise<Array>}
     */
    async fetchFavorites(publicationType) {
        const result = await get(
            `${placeholders.router.baseUri}/ajax-data/account/favorites`,
            {
                typeId: publicationType
            }
        );

        return result?.publications ?? [];
    }

    /**
     * Toggle a publication via the backend and sync cache.
     *
     * @param {String} publicationType
     * @param {String} publicationId
     * @return {Promise<{state: boolean}>}
     */
    async togglePublicationRequest(publicationType, publicationId) {
        const result = await post(
            `${placeholders.router.baseUri}/ajax-data/account/like-toggle`,
            {
                objectType: publicationType,
                objectId: publicationId
            }
        );

        this.favorites[publicationType] = await this.getFavorites(publicationType, true);

        this.dispatchStateChanged(publicationType, publicationId, result.state);

        return result;
    }

    /**
     * Dispatch a global event so every like badge can sync itself.
     *
     * @param {String} publicationType
     * @param {String} publicationId
     * @param {boolean} state
     */
    dispatchStateChanged(publicationType, publicationId, state) {
        document.dispatchEvent(new CustomEvent(FAVORITE_STATE_CHANGED_EVENT, {
            detail: {
                publicationType: String(publicationType),
                publicationId: String(publicationId),
                state: Boolean(state),
                favorites: this.favorites[String(publicationType)] ?? null
            }
        }));
    }

    /**
     * Check if a publication is favorited.
     *
     * @param {Number|String} publicationType
     * @param {String} publicationId
     * @return {Promise<boolean>}
     */
    async isFavorited(publicationType, publicationId) {
        const type = String(publicationType);
        const id = String(publicationId);

        const favorites = await this.getFavorites(type);
        return favorites.some(item => String(item.hashIdEncrypted) === id);
    }
}

const FavoritesManager = new FavoritesManagerClass();

export default FavoritesManager;
